import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import styles from "../styles";
import { useTranslation } from "react-i18next";
import GetApp from "@material-ui/icons/GetApp";
import { get, set, over } from "@totalsoft/change-tracking-react";
import { useClientQueryWithErrorHandling } from "hooks/errorHandling";
import { convertBase64ToFile } from "utils/fileConverter";
import fileSaver from "file-saver";
import { DOWNLOAD_REQUIRED_DOCUMENT_TEMPLATE_QUERY, DOWNLOAD_DMS_FILE_QUERY } from "../queries";
import { remove } from "ramda";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { REMOVE_REQUIRED_DOCUMENT, UPLOAD_REQUIRED_DOCUMENT } from "../mutations";
import { useApolloClient, gql } from "@apollo/client";
import cx from "classnames";
import UploadDocuments from "features/common/components/UploadDocuments";
import DownloadOrDeleteDocument from "features/common/components/DownloadOrDeleteDocument";
import { addToast } from "components/notifications";
import { quoteSteps } from "features/quote/constants";

const useStyles = makeStyles(styles);

function RequiredDocumentComponent(props) {
  const { requiredDocumentLens, index, disabled, quoteId } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();

  const [removeFile] = useMutationWithErrorHandling(REMOVE_REQUIRED_DOCUMENT);

  const handleDeleteFile = useCallback(
    (sharePointUID, index) => {
      removeFile({ variables: { quoteId, sharePointUID } });
      over(requiredDocumentLens.files, remove(index, 1));
    },
    [requiredDocumentLens, removeFile, quoteId]
  );

  const handleDownloadFile = useCallback(
    async (sharePointUID, fileName) => {
      const { data } = await clientQuery(DOWNLOAD_DMS_FILE_QUERY, {
        variables: { quoteId, sharePointUID, fileName, quoteStep: quoteSteps.dataCompletion },
        fetchPolicy: "no-cache",
      });

      if (!data) return;

      const fileURL = convertBase64ToFile(data.downloadDmsFile.content);
      fileSaver.saveAs(fileURL, data.downloadDmsFile.name);
    },
    [clientQuery, quoteId]
  );

  const handleDownloadFormTemplate = useCallback(async () => {
    const { data } = await clientQuery(DOWNLOAD_REQUIRED_DOCUMENT_TEMPLATE_QUERY, {
      variables: {
        quoteId,
        requiredDocTypeId: requiredDocumentLens.requiredDocTypeId |> get,
        requiredDocContentType: requiredDocumentLens.requiredDocContentType |> get,
      },
      fetchPolicy: "no-cache",
    });

    if (!data) return;

    const fileURL = convertBase64ToFile(data.downloadRequiredDocumentTemplate.content);
    fileSaver.saveAs(fileURL, data.downloadRequiredDocumentTemplate.name);
  }, [quoteId, requiredDocumentLens, clientQuery]);

  const [uploadFile] = useMutationWithErrorHandling(UPLOAD_REQUIRED_DOCUMENT, {
    onCompleted: (data) => {
      const currentFiles = requiredDocumentLens.files |> get;
      const newFiles = [...currentFiles];
      data.uploadRequiredDocument.forEach((file) => {
        newFiles.push(file);
      });
      (requiredDocumentLens.files |> set)(newFiles);

      client.cache.modify({
        id: client.cache.identify({
          __typename: "RequiredDocument",
          quoteId,
          id: requiredDocumentLens.id |> get,
          guarantorId: requiredDocumentLens.guarantorId |> get,
        }),
        fields: {
          files(filesRef) {
            data.uploadRequiredDocument.forEach((file) => {
              filesRef = [
                ...filesRef,
                client.cache.writeFragment({
                  data: file,
                  fragment: gql`
                    fragment NewFile on DmsFile {
                      sharePointUID
                      fileName
                    }
                  `,
                }),
              ];
            });
            return [...filesRef];
          },
        },
      });
    },
  });

  const handleUpload = useCallback(
    async (files) => {
      if (files.length <= 0) return false;

      const uploadedFiles = files.map((file) => {
        return file.file;
      });

      await uploadFile({
        variables: {
          quoteId,
          dmsContentType: requiredDocumentLens.requiredDocContentType |> get,
          targetAreaCode: requiredDocumentLens.targetAreaCode |> get,
          uploadedFiles,
        },
      });
      addToast(t("ToastNotifications.Success"), "success");
    },
    [quoteId, requiredDocumentLens, uploadFile, t]
  );

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      direction="row"
      className={index === 0 ? classes.firstReqDocBorder : classes.restOfReqDocBorder}
    >
      <Grid item>
        <Typography variant="h6" className={classes.reqText}>
          {requiredDocumentLens.caption |> get}
        </Typography>
      </Grid>
      <Grid item>
        {(requiredDocumentLens.isMandatory |> get) && (
          <Tooltip title={t("General.MandatoryDocument")} placement="top">
            <Typography variant="h6" className={cx(classes.reqText, classes.reqTextRed)}>
              {"*"}
            </Typography>
          </Tooltip>
        )}
      </Grid>
      <Grid item container xs alignItems="center" justifyContent="flex-end">
        {(requiredDocumentLens.requiredDocFileName |> get) && (
          <IconButton onClick={handleDownloadFormTemplate} variant="outlined" className={classes.rightAlign}>
            <Tooltip title={t("General.FormTemplates")} placement="top">
              <GetApp fontSize="small" />
            </Tooltip>
          </IconButton>
        )}
        <UploadDocuments disabled={disabled} onUpload={handleUpload} />
      </Grid>
      {(requiredDocumentLens.files |> get).map((file, index) => (
        <DownloadOrDeleteDocument
          key={index}
          index={index}
          file={file}
          onDownloadFile={handleDownloadFile}
          onDeleteFile={handleDeleteFile}
          disabled={disabled}
        />
      ))}
    </Grid>
  );
}

RequiredDocumentComponent.propTypes = {
  requiredDocumentLens: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  quoteId: PropTypes.number,
  disabled: PropTypes.bool.isRequired,
};

export default RequiredDocumentComponent;
